/**
 * MapSearchBar — floating search input for the map.
 *
 * Renders MapboxSearch at the same height as a filter pill (`h-14`). The map
 * top bar places it; this component does not pin itself to the viewport.
 *
 * On selection of a result the map flies to the result coordinates. The result
 * is then handled one of two ways:
 *   - workspace active (`onWorkspaceResult` provided) → the parent launches a
 *     compute run at the result immediately; no ephemeral marker is placed
 *     (the optimistic marker from the add-locations mutation renders the pin).
 *   - no workspace → a single ephemeral navigation marker is placed; no data is
 *     written and no zone is selected.
 *
 * With `onZoneSelect` set, the results list carries a second, separately
 * labelled section above the address results: the hunting zones matching the
 * typed text (`MapZoneSuggestions`). Picking one selects the zone and stops
 * there — no flyTo, no marker, no compute run.
 *
 * With `onDealSelect` set, the same list carries the mirrored deals matching
 * the typed text (`MapDealSuggestions`). Picking one opens the deal card and
 * eases the camera onto the deal when it is placed — the BD asked for it by
 * name and has no idea where it sits.
 */
import { SlidersHorizontal } from 'lucide-react';
import mapboxgl, { type LngLatLike } from 'mapbox-gl';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { ComputePreferencesModal } from '@/features/map/components/ComputePreferencesModal';
import { MapDealSuggestions } from '@/features/map/components/MapDealSuggestions';
import { MapZoneSuggestions } from '@/features/map/components/MapZoneSuggestions';
import {
  cameraDuration,
  DEAL_FOCUS_DURATION_MS,
  DEAL_FOCUS_ZOOM,
} from '@/features/map/utils/cameraMotion';
import { zoneIdAtLngLat } from '@/features/map/utils/zoneIdAtLngLat';
import { MapboxSearch } from '@/features/mapbox/components/MapboxSearch';
import {
  type AddressLines,
  featureAddressLines,
} from '@/features/mapbox/utils/featureAddressLines';
import { featureOrCoordToLngLat } from '@/features/mapbox/utils/featureOrCoordToLngLat';
import { cn } from '@/libs/utils/cn';
import type { IDealSummary } from '@/types/api';
import type { SearchBoxFeatureSuggestion } from '@/types/mapbox';

// ── Constants ────────────────────────────────────────────────────────────────

/** Street/neighbourhood zoom level used when flying to a search result. */
export const MAP_SEARCH_FLYTO_ZOOM = 14;

// ── Props ────────────────────────────────────────────────────────────────────

interface MapSearchBarProps {
  shell: MapShellRenderState;
  /**
   * When provided (a workspace is active), a successful search result
   * launches a compute run at the result INSTEAD OF placing the ephemeral
   * navigation-only search marker. When undefined (no active workspace),
   * search stays navigation-only.
   */
  onWorkspaceResult?: (
    coords: { latitude: number; longitude: number },
    zoneId: string | null,
    addressLines?: AddressLines | null,
  ) => void;
  /**
   * When provided, the results list also offers hunting zones matching the
   * typed text, above the address results. Picking one reports its id and
   * nothing else — zone selection is a URL concern (`?zone=<id>`), and the
   * camera work that follows belongs to `MapZoneFocus` / `MapCamera`.
   */
  onZoneSelect?: (zoneId: string) => void;
  /**
   * When provided, the results list also offers the mirrored deals matching
   * the typed text. Picking one reports its id — opening the card is a URL
   * concern (`?deal=<id>`) — and the bar eases the camera onto a placed deal.
   */
  onDealSelect?: (dealId: string) => void;
  /** Disables the search input — set while a compute run is active, or while no workspace is selected. */
  disabled?: boolean;
  /**
   * Whether `disabled` is caused by an active compute run — shows the
   * animated glow. When `disabled` is set without this (e.g. no workspace
   * selected), the input renders a plain blocked state with an explanatory
   * placeholder instead.
   */
  isComputing?: boolean;
  /** Reports whether the address-results popover is open. */
  onResultsOpenChange?: (open: boolean) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function MapSearchBar({
  shell,
  onWorkspaceResult,
  onZoneSelect,
  onDealSelect,
  disabled = false,
  isComputing = false,
  onResultsOpenChange,
}: MapSearchBarProps) {
  // A single marker ref — at most one search-result pin is shown at a time.
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  // Compute-preferences modal open state.
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Remove the current marker (if any). Stable reference via useCallback so
  // map.on/map.off('click', clearMarker) reference the same function (symmetric
  // teardown). A no-op when no marker is present.
  const clearMarker = useCallback(() => {
    markerRef.current?.remove();
    markerRef.current = null;
  }, []);

  // Place (or replace) a marker at [lng, lat]. Removes any existing pin first
  // so only one search-result marker is ever present on the map.
  function placeMarker(lngLat: [number, number], map: mapboxgl.Map): void {
    markerRef.current?.remove();
    markerRef.current = new mapboxgl.Marker({ anchor: 'bottom' }).setLngLat(lngLat).addTo(map);
  }

  // Bind Escape + map-click dismissal once the map is ready. Bound
  // unconditionally (not scoped to a marker flag): clearMarker is a no-op with
  // no marker and uses no stopPropagation/preventDefault, so it never suppresses
  // MapSurface's zone-close Escape or MapShell's selection-clear. isMapLoaded
  // re-binds onto a fresh map instance if the shell ever reloads its map;
  // clearMarker is stable.
  useEffect(() => {
    const map = shell.map.current;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') clearMarker();
    }

    window.addEventListener('keydown', onKeyDown);
    map?.on?.('click', clearMarker);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      map?.off?.('click', clearMarker);
    };
  }, [shell.map, shell.isMapLoaded, clearMarker]);

  // Remove any marker on unmount — no leak.
  useEffect(
    () => () => {
      markerRef.current?.remove();
    },
    [],
  );

  // Clearing the search input removes the marker. MapboxSearch owns
  // the input; we observe the bubbled `input` event at the container div so
  // we do not need to fork MapboxSearch or add a prop to it.
  function handleContainerInput(event: FormEvent<HTMLDivElement>): void {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.value === '' && markerRef.current != null) {
      clearMarker();
    }
  }

  // Fly to a validated [lng, lat] result, then EITHER launch compute via the
  // parent (workspace active) OR place the ephemeral navigation-only marker
  // (no workspace) — never both, so the two outcomes are mutually exclusive.
  function flyToResult(
    lng: number,
    lat: number,
    map: mapboxgl.Map,
    addressLines?: AddressLines | null,
  ): void {
    // Zone hit-test before flyTo starts animating: queries the zones layer at
    // the CURRENT camera position. A result far outside the current viewport
    // may not have its zone tile loaded yet, in which case this reads null
    // even if the destination is in fact inside a zone — acceptable, since
    // that only affects the Mixpanel zone_id attribution, not behaviour.
    const zoneId = zoneIdAtLngLat(map, { lng, lat });
    map.flyTo({ center: [lng, lat], zoom: MAP_SEARCH_FLYTO_ZOOM });
    if (onWorkspaceResult != null) {
      onWorkspaceResult({ latitude: lat, longitude: lng }, zoneId, addressLines);
    } else {
      placeMarker([lng, lat], map);
    }
  }

  // Handle a Mapbox Search Box selection: a feature suggestion or a pasted
  // coordinate pair. Guard: the map must be ready before any navigation or
  // marker work. featureOrCoordToLngLat owns the discrimination +
  // Point/finite/WGS-84 guards.
  function handleSelect(value: SearchBoxFeatureSuggestion | LngLatLike): void {
    const map = shell.map.current;
    if (!shell.isMapLoaded || map == null) {
      return;
    }
    const lngLat = featureOrCoordToLngLat(value);
    if (lngLat == null) {
      return;
    }
    flyToResult(lngLat[0], lngLat[1], map, featureAddressLines(value));
  }

  // A deal is a different destination than the address that may have dropped
  // a pin, so the pin goes. An unplaced deal opens where the camera already is.
  function handleDealPick(deal: IDealSummary): void {
    clearMarker();
    const map = shell.map.current;
    if (shell.isMapLoaded && map != null && deal.latitude != null && deal.longitude != null) {
      map.easeTo({
        center: [deal.longitude, deal.latitude],
        zoom: DEAL_FOCUS_ZOOM,
        duration: cameraDuration(DEAL_FOCUS_DURATION_MS),
      });
    }
    onDealSelect?.(deal.id);
  }

  // The animated glow reads as "in progress" — only show it for an actual
  // compute run, not for the plain no-workspace block.
  const showComputeGlow = disabled && isComputing;
  const blocked = disabled && !isComputing;

  return (
    // stopPropagation prevents map-canvas click handlers from firing when the
    // user interacts with the search box.
    <div
      role="search"
      aria-label="Map map search"
      data-testid="map-search-bar"
      className={cn('relative flex h-14 w-full min-w-0 items-center', blocked && 'opacity-40')}
      onClick={(e) => e.stopPropagation()}
      onInput={handleContainerInput}
    >
      {/* The ring's padding sits outside the field: a fixed-height box with overflow-hidden clips it. */}
      <div
        className={cn(
          'min-w-0 flex-1',
          showComputeGlow && 'relative overflow-hidden rounded-[12px] p-[2px]',
        )}
      >
        {showComputeGlow && (
          <div
            aria-hidden="true"
            data-testid="map-search-compute-glow"
            className="absolute inset-[-150%] animate-spin"
            style={{
              animationDuration: '2.4s',
              background:
                'conic-gradient(from 0deg, transparent 0deg, var(--brand) 45deg, transparent 100deg)',
              filter: 'drop-shadow(0 0 4px var(--brand)) drop-shadow(0 0 10px var(--brand))',
            }}
          />
        )}
        <div className={cn('relative', showComputeGlow && 'rounded-[10px] bg-background')}>
          <MapboxSearch
            onSelect={handleSelect}
            onOpenChange={onResultsOpenChange}
            disabled={disabled}
            className="[&_input]:h-14 [&_input]:min-h-14 [&_input]:disabled:opacity-100"
            resultsSlot={
              onZoneSelect != null || onDealSelect != null
                ? ({ query, close }) => (
                    <>
                      {onZoneSelect != null && (
                        <MapZoneSuggestions
                          query={query}
                          onSelectZone={(zoneId) => {
                            // A zone is a different destination than the address
                            // that may have dropped a pin — clear it so the map is
                            // not left with a stale marker over the focused zone.
                            clearMarker();
                            onZoneSelect(zoneId);
                            close();
                          }}
                        />
                      )}
                      {onDealSelect != null && (
                        <MapDealSuggestions
                          query={query}
                          onSelectDeal={(deal) => {
                            handleDealPick(deal);
                            close();
                          }}
                        />
                      )}
                    </>
                  )
                : undefined
            }
            placeholder={blocked ? 'Select a workspace to search' : undefined}
            leftSlot={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Compute preferences"
                  title="Compute preferences"
                  onClick={() => setPreferencesOpen(true)}
                  className="flex size-5 items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <SlidersHorizontal className="size-5" aria-hidden="true" />
                </button>
                <div className="h-6 w-px bg-border" aria-hidden="true" />
              </div>
            }
          />
        </div>
      </div>
      <ComputePreferencesModal
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        dialogContainer={shell.containerRef.current ?? undefined}
      />
    </div>
  );
}
